import React, { useMemo, useCallback } from 'react';
import { Box, Typography, Button, Alert, Stack, TextField, InputAdornment } from '@mui/material';
import {
  Add as AddIcon,
  Help as HelpIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
  Selection,
  VirtualScroll,
  Sort,
} from '@syncfusion/ej2-react-grids';

import { useAppSelector } from '../../../../../../app/store';
import { useGetCarveOutsByContractIdQuery, selectCurrentContract } from '../../../../../../services/ContractService';
import { useSignalR } from '../../../../../../hooks/useSignalR';
import { AddEditCarveOutDialog } from '../../../dialogs';
import CarveOutUploadCsv from '../../../commands/contractCommands/CarveOutUploadCsv';
import CarveOutDownloadCsv from '../../../commands/contractCommands/CarveOutDownloadCsv';
import CarveOutHelpDialog from '../../../dialogs/contractDialogs/CarveOutHelpDialog';
import CarveOutUploadProgress from '../../../dialogs/contractDialogs/CarveOutUploadProgress';
import { useCarveOutSearch, useCarveOutDialog, useCarveOutUpload } from './hooks';
import { rateTemplate, multiProcDiscountTemplate, caseRateTemplate } from './templates';

export interface CarveOutGridProps {
  testId?: string;
}

/**
 * CarveOutGrid - Displays and manages carve-out codes for a contract
 *
 * Follows the established grid pattern with:
 * - Separated templates for cell rendering
 * - Custom hooks for search, dialog, and upload handling
 * - SignalR integration for upload progress
 */
export const CarveOutGrid: React.FC<CarveOutGridProps> = ({ testId }) => {
  const selectedContract = useAppSelector(selectCurrentContract);

  // Fetch carve-outs
  const { data: carveOuts = [], isLoading, isError } = useGetCarveOutsByContractIdQuery(
    { contractId: selectedContract?.id || '' },
    { skip: !selectedContract?.id }
  );

  // Custom hooks
  const { searchTerm, filteredCarveOuts, handleSearchChange, handleClearSearch } =
    useCarveOutSearch(carveOuts);

  const {
    dialogOpen,
    editingCarveOut,
    helpDialogOpen,
    handleAddCarveOut,
    handleEditCarveOut,
    handleCloseDialog,
    handleOpenHelpDialog,
    handleCloseHelpDialog,
  } = useCarveOutDialog();

  const {
    uploadProgress,
    isUploadInProgress,
    onProgress,
    onCompleted,
    onError,
    handleCloseProgressDialog,
  } = useCarveOutUpload();

  // SignalR connection
  const { isConnected: isSignalRConnected } = useSignalR({
    serviceName: 'Contracts',
    uploadType: 'CarveOut',
    onProgress,
    onCompleted,
    onError,
    autoConnect: true,
  });

  // Handle row selection for editing
  const handleRowSelection = useCallback(
    (args: { data?: { id: string } }) => {
      if (args.data) {
        const selectedCarveOut = filteredCarveOuts.find((c) => c.id === args.data!.id);
        if (selectedCarveOut) {
          handleEditCarveOut(selectedCarveOut);
        }
      }
    },
    [filteredCarveOuts, handleEditCarveOut]
  );

  // Transform data for grid display
  const gridData = useMemo(() => {
    return filteredCarveOuts
      .map((carveOut) => ({
        id: carveOut.id,
        code: carveOut.code,
        description: carveOut.description,
        rate: carveOut.rate,
        applyMultiProcedureDisc: carveOut.applyMultiProcedureDisc,
        isCaseRate: carveOut.isCaseRate,
      }))
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [filteredCarveOuts]);

  const gridSettings = {
    allowPaging: true,
    allowSorting: true,
    enableVirtualization: true,
    pageSettings: { pageSize: 100, pageSizes: [50, 100, 200, 500] },
    selectionSettings: { type: 'Single' as const, mode: 'Row' as const },
    rowHeight: 32,
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}
        >
          Carve-Outs ({carveOuts.length})
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddCarveOut} size="small">
            Add Carve-Out
          </Button>
          <CarveOutUploadCsv />
          <CarveOutDownloadCsv />
          <Button variant="outlined" startIcon={<HelpIcon />} onClick={handleOpenHelpDialog} size="small">
            Help
          </Button>
        </Stack>
      </Stack>

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SearchIcon sx={{ color: '#64748b' }} />
          <TextField
            fullWidth
            size="small"
            placeholder="Search by code, description, or rate..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            InputProps={{
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <Button size="small" onClick={handleClearSearch}>
                    <ClearIcon fontSize="small" />
                  </Button>
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Box>

      {carveOuts.length > 0 && (
        <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic', mb: 1, fontSize: '0.75rem' }}>
          Click on any row to edit or delete a carve-out
        </Typography>
      )}

      {carveOuts.length === 0 ? (
        <Alert severity="info">
          No carve-outs defined yet. Click "Add Carve-Out" or upload a CSV file.
        </Alert>
      ) : (
        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
          <GridComponent
            dataSource={gridData}
            {...gridSettings}
            height="400"
            width="100%"
            rowSelected={handleRowSelection}
            style={{ border: 'none' }}
            data-testid={testId}
          >
            <ColumnsDirective>
              <ColumnDirective field="code" headerText="Code" width="120" textAlign="Left" />
              <ColumnDirective field="description" headerText="Description" width="250" />
              <ColumnDirective field="rate" headerText="Rate" width="120" textAlign="Right" template={rateTemplate} />
              <ColumnDirective
                field="applyMultiProcedureDisc"
                headerText="Multi-Proc Disc"
                width="130"
                textAlign="Center"
                template={multiProcDiscountTemplate}
              />
              <ColumnDirective
                field="isCaseRate"
                headerText="Case Rate"
                width="100"
                textAlign="Center"
                template={caseRateTemplate}
              />
            </ColumnsDirective>
            <Inject services={[Page, Selection, VirtualScroll, Sort]} />
          </GridComponent>
        </Box>
      )}

      <AddEditCarveOutDialog
        open={dialogOpen}
        editingCarveOut={editingCarveOut}
        contractId={selectedContract?.id || ''}
        onClose={handleCloseDialog}
      />

      <CarveOutHelpDialog open={helpDialogOpen} onClose={handleCloseHelpDialog} />

      {uploadProgress && (
        <CarveOutUploadProgress
          open={!!uploadProgress}
          progress={uploadProgress?.progressPercentage || 0}
          status={uploadProgress?.message || 'Preparing upload...'}
          error={uploadProgress?.status === 'Failed' ? uploadProgress.message : undefined}
          success={uploadProgress?.progressPercentage === 100}
          uploadCount={uploadProgress?.processedRecords}
          totalCount={uploadProgress?.totalRecords}
          onClose={handleCloseProgressDialog}
        />
      )}
    </Box>
  );
};

export default CarveOutGrid;
